import { useMemo, useState } from "react";
import { IonContent, IonPage } from "@ionic/react";
import { Redirect, useParams } from "react-router-dom";

import { Hero } from "../components/Hero";
import { NavRow, RowGroup } from "../components/Rows";
import { Segmented } from "../components/Segmented";
import { Screen } from "../components/Shell";
import { TopBar } from "../components/TopBar";
import { todayStr } from "../domain/date";
import {
  recordsNote,
  SERIES_LABEL,
  isRecordSeries,
  recordLine,
  records,
  recordsHero,
  recordTitle,
  recordValue,
  type RecordSeries,
} from "../domain/records";
import { useKaya } from "../store/kayaStore";
import "./Progress.css";

type RecordWindow = 28 | 90 | 0;

const WINDOWS: { value: RecordWindow; label: string }[] = [
  { value: 28, label: "28 days" },
  { value: 90, label: "90 days" },
  { value: 0, label: "All" },
];

const BACK: Record<RecordSeries, string> = {
  steps: "Back to Movement",
  active: "Back to Training",
  energy: "Back to Energy",
  water: "Back to Water",
};

/**
 * /progress/records/:series (ADR-061) — every day's exact figure, as rows: the owner could see
 * the shape of a month on a chart but not read a past day's number anywhere. One answer (the
 * window's average), then a group a week, Monday to Sunday, newest first, every day present so a
 * gap shows as "–". Nothing here is a chart.
 */
export default function ProgressRecords() {
  const { series } = useParams<{ series: string }>();
  const S = useKaya((s) => s.S);
  const [w, setW] = useState<RecordWindow>(28);
  const today = todayStr();
  const known = isRecordSeries(series) ? series : null;
  const r = useMemo(() => (known ? records(S, known, today, w) : null), [S, known, today, w]);

  if (!known || !r) return <Redirect to="/progress" />;
  const hero = recordsHero(r, w);
  const label = SERIES_LABEL[known];

  return (
    <IonPage>
      <IonContent>
        <Screen>
          <TopBar title={label.title} back={{ to: label.back, label: BACK[known] }} />
          <div className="pg">
            <Hero
              ariaLabel={`${hero.label}: ${hero.figure ?? "none yet"} ${hero.unit}`}
              label={hero.label}
              figure={hero.figure}
              unit={hero.unit}
              line={hero.line}
            />

            <Segmented options={WINDOWS} value={w} onChange={setW} ariaLabel="Records window" />

            {r.weeks.map((wk) => (
              <RowGroup key={wk.from} heading={wk.heading}>
                {wk.days.map((d) => (
                  <NavRow
                    key={d.ds}
                    kind="value"
                    title={recordTitle(d, today)}
                    subtitle={recordLine(known, d) || undefined}
                    value={recordValue(known, d)}
                  />
                ))}
              </RowGroup>
            ))}

            <p className="pg-note">{recordsNote(known)}</p>
          </div>
        </Screen>
      </IonContent>
    </IonPage>
  );
}
